import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Users, Truck, X,
  Loader2,
  TrendingUp,
  Award, Flame, Trophy,
  Settings, Zap, Star, Trash2, Tag, FileText,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// ── Existing sub-component imports ────────────────────────────────────────────
import { ChatRoom } from "./market/ChatRoom";
import { TourWizard, useTour } from "./market/TourWizard";
import { SafetyHub } from "./market/SafetyHub";
import { RecruiterHub } from "./market/RecruiterHub";
import { DriversHub } from "./market/DriverHub";
import { AdminPanel } from "./market/AdminPanel";
import { PendingReceiptsPanel } from "./market/PendingReceiptsPanel";
import { ProfilesPanel } from "./market/ProfilesPanel";
import { EmailAccountsPanel } from "./market/EmailAccountsPanel";
import { PaymentStatusPanel } from "./market/PaymentStatusPanel";
import { AgreementsPanel } from "./market/AgreementsPanel";
import { CarrierHub } from "./market/CarrierHub";
import { TourEditor } from "./market/TourEditor";
import { TransferPaymentModal } from "./market/TransferPaymentModal";
import { CarrierAgreementModal } from "./market/CarrierAgreementModal";
import { StaffAgreementModal }  from "./market/StaffAgreementModal";
import { AnnulmentNoticeModal }  from "./market/AnnulmentNoticeModal";
import { CarrierDocUploadModal } from "./market/CarrierDocUploadModal";

// ── New extracted component imports ───────────────────────────────────────────
import {
  MARKET_TOKEN_KEY, MARKET_TERMS_KEY,
  ACCENT, GOLD, DARK, MID,
  STAGGER_CONTAINER, FADE_UP, SLIDE_LEFT,
} from "./market/constants";
import {
  MarketStyleTag, FloatingOrbs, GrainOverlay, ScanlineBeam,
  timeAgo, useCountUp, ShimmerSweep, LiveDot,
} from "./market/MarketUI";
import { CarrierDriverProfile } from "./market/CarrierDriverProfile";
import { HubSidebar } from "./market/HubSidebar";
import { BidModal } from "./market/BidModal";
import { SetupWizard, DriverSelfApplyModal, LoginModal, TermsGate, ResetPasswordModal } from "./market/AuthModals";

// ══════════════════════════════════════════════════════════════════════════════
// ── MARKET PAGE (Orchestration Shell) ─────────────────────────────────────────
// ══════════════════════════════════════════════════════════════════════════════
export default function Market() {
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref") || "";

  const [setupComplete, setSetupComplete]   = useState(false);
  const [marketUser, setMarketUser]         = useState(null);
  const [authLoading, setAuthLoading]       = useState(true);
  const [showSetup, setShowSetup]           = useState(false);
  const [showLogin, setShowLogin]           = useState(false);
  const [loginHub, setLoginHub]             = useState("carrier");
  const [resetToken, setResetToken]         = useState(() => new URLSearchParams(window.location.search).get("reset_token") || "");
  // Pending bid slug — from ?bid=joseph-harris on the URL (driver share page CTA)
  const pendingBidSlugRef = useRef(new URLSearchParams(window.location.search).get("bid") || "");
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Emergent Auth returns session_id in URL hash (#session_id=...), NOT query params
  const [googleSessionId, setGoogleSessionId] = useState(() => {
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const match = hash.match(/session_id=([^&]+)/);
      return match ? decodeURIComponent(match[1]) : "";
    }
    return "";
  });
  const [googleAuthLoading, setGoogleAuthLoading] = useState(false);
  // Tour wizard — auto-shows on first login per hub
  const [showTour, openTour, closeTour] = useTour(marketUser?.hub || null);
  const [showTerms, setShowTerms]           = useState(false);
  const [pendingToken, setPendingToken]     = useState(null);
  const [showBidModal, setShowBidModal]     = useState(false);
  const [bidDriver, setBidDriver]           = useState(null);
  const [deletingBidId, setDeletingBidId]   = useState(null); // Live Bid Board per-row confirm
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [drivers, setDrivers]               = useState([]);
  const [bids, setBids]                     = useState([]);
  const [dataLoading, setDataLoading]       = useState(true);
  const [showAdmin, setShowAdmin]           = useState(false);
  const [adminTab, setAdminTab]             = useState("market"); // "market" | "receipts" | "payment" | "profiles" | "email" | "tour"
  const [pendingReceiptsCount, setPendingReceiptsCount] = useState(0);
  const lastToastedCountRef = React.useRef(0);
  // adminViewAs: when admin clicks another hub they "enter" it without a new login
  const [adminViewAs, setAdminViewAs]       = useState(null); // null | "safety" | "recruiter" | "driver" | "carrier"
  const [docCredits, setDocCredits]         = useState(null);   // {credits, pending_count, available_total} — passed to CarrierHub
  const [carrierDocUploadBid, setCarrierDocUploadBid] = useState(null); // bid object — drives CarrierDocUploadModal
  const [showAgreement, setShowAgreement]           = useState(false);
  const [showStaffAgreement, setShowStaffAgreement] = useState(false);
  const [pendingBidDriver, setPendingBidDriver] = useState(null);
  // postTermsPendingDriver: driver waiting to be bid on AFTER terms are accepted
  const [postTermsPendingDriver, setPostTermsPendingDriver] = useState(null);
  const [showDriverJoin, setShowDriverJoin] = useState(false);
  const [transferAlerts, setTransferAlerts] = useState([]);   // drivers bid on but taken by another carrier
  // Transfer payment notifications
  const [paymentNotifs, setPaymentNotifs]           = useState([]);
  const [paymentNotifIndex, setPaymentNotifIndex]   = useState(0);
  const [isPaymentMinimized, setIsPaymentMinimized] = useState(false);
  const [paymentNotif, setPaymentNotif]             = useState(null);   // manual My-Bids upload
  const [showPaymentModal, setShowPaymentModal]     = useState(false);  // manual My-Bids upload
  // Annulment notices (carrier)
  const [annulmentNotifs, setAnnulmentNotifs]       = useState([]);
  const [annulNotifIndex, setAnnulNotifIndex]       = useState(0);
  const [annulAcking, setAnnulAcking]               = useState(false);
  const [annulmentHistory, setAnnulmentHistory]     = useState([]);

  // Count-up animated values for hero stats
  const driverCountDisplay = useCountUp(drivers.length, 1200);
  const bidCountDisplay    = useCountUp(bids.filter(b => b.status === "live").length, 1400);
  const [hiredThisMonth, setHiredThisMonth]   = useState(0);
  const hiredCountDisplay = useCountUp(hiredThisMonth, 1600);
  const [leaderboard, setLeaderboard]         = useState([]);
  // Driver to auto-bid after login (from ?bid= URL param when user wasn't logged in)
  const [slugPendingDriver, setSlugPendingDriver] = useState(null);

  // ── Bootstrap ──
  useEffect(() => {
    (async () => {
      try {
        const { setup_complete } = await api.marketSetupStatus();
        setSetupComplete(setup_complete);
        const tok = localStorage.getItem(MARKET_TOKEN_KEY) || sessionStorage.getItem(MARKET_TOKEN_KEY);
        if (tok) {
          try {
            const u = await api.marketVerifyToken(tok);
            setMarketUser(u);
            const termsOk = u.terms_accepted || localStorage.getItem(MARKET_TERMS_KEY) === "true";
            if (!termsOk) { setPendingToken(tok); setShowTerms(true); }
            // Gate: unsigned carrier hits agreement modal immediately on hub entry
            else if (u.hub === "carrier" && !u.carrier_agreement_signed) { setShowAgreement(true); }
            // Gate: unsigned recruiter/setter hits staff agreement modal
            else if ((u.hub === "recruiter" || u.hub === "setter") && !u.staff_agreement_signed) { setShowStaffAgreement(true); }
          } catch { localStorage.removeItem(MARKET_TOKEN_KEY); }
        }
      } finally { setAuthLoading(false); }
    })();
  }, []);

  // ── Auto-open Carrier Hub when arriving from a driver share link ──
  // Triggered by ?open_carrier=1 (added by DriverPublicProfile "Sign Up as Carrier" CTA)
  useEffect(() => {
    if (authLoading) return; // wait until token check is done
    if (marketUser) return;  // already logged in — nothing to do
    if (searchParams.get("open_carrier") === "1") {
      // Give the page a tick to finish mounting then trigger carrier hub login flow
      const timer = setTimeout(() => {
        handleHubClick("carrier", setupComplete);
      }, 400);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, marketUser, setupComplete]);
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Emergent Auth returns session_id in URL hash (#session_id=...) — read from window.location.hash
  useEffect(() => {
    if (!googleSessionId) return;
    // Clean URL hash immediately so refresh doesn't re-trigger
    window.history.replaceState({}, "", window.location.pathname);
    setGoogleAuthLoading(true);
    (async () => {
      try {
        const res = await api.marketGoogleAuth(googleSessionId, "carrier");
        localStorage.setItem(MARKET_TOKEN_KEY, res.token);
        onLoginSuccess(res);
        toast.success(`Welcome, ${res.carrier_name || res.name || "Carrier"}! You're now logged in.`);
        // Scroll carrier hub into view so user knows they're in
        setTimeout(() => {
          document.getElementById("carrier-hub-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 400);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : "Google sign-in failed. Please try again.");
      } finally {
        setGoogleSessionId("");
        setGoogleAuthLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleSessionId]);

  // ── Load market data ──
  const loadMarketData = useCallback(async () => {
    try {
      const [driversData, bidsData] = await Promise.all([
        api.marketGetDrivers(),
        api.marketGetBids(),
      ]);
      setDrivers(driversData);
      setBids(bidsData);
    } catch { /* ignore */ } finally { setDataLoading(false); }
  }, []);

  // ── Load carrier transfer alerts ──
  const loadTransferAlerts = useCallback(async () => {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY) || sessionStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok || !marketUser || !["carrier", "admin"].includes(marketUser.hub)) return;
    try {
      const res = await api.marketCarrierTransferAlerts(tok);
      setTransferAlerts(res.alerts || []);
    } catch { /* ignore */ }
  }, [marketUser]);

  useEffect(() => { loadMarketData(); }, [loadMarketData]);

  // ── Handle ?bid=slug from driver public profile CTA ──────────────────────
  useEffect(() => {
    const slug = pendingBidSlugRef.current;
    if (!slug || drivers.length === 0) return;
    const driver = drivers.find((d) =>
      d.public_slug === slug ||
      `${d.first_name}-${d.last_name}`.toLowerCase().replace(/\s+/g, "-") === slug
    );
    if (!driver) return;
    pendingBidSlugRef.current = ""; // consume once
    window.history.replaceState({}, "", window.location.pathname);
    openBidModal(driver);
    // Scroll driver board into view for context
    setTimeout(() => {
      document.getElementById("tour-driver-board")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drivers]);
  useEffect(() => {
    const id = setInterval(loadMarketData, 5000);
    return () => clearInterval(id);
  }, [loadMarketData]);

  // ── Load hired-this-month stat ──
  useEffect(() => {
    api.marketGetHiredStats().then(r => setHiredThisMonth(r.hired_this_month || 0)).catch(() => {});
  }, []);

  // ── Load carrier leaderboard ──
  useEffect(() => {
    api.marketGetCarrierLeaderboard().then(r => setLeaderboard(r.leaderboard || [])).catch(() => {});
  }, []);

  // Poll transfer alerts every 30 sec when carrier is logged in
  useEffect(() => { loadTransferAlerts(); }, [loadTransferAlerts]);
  useEffect(() => {
    const id = setInterval(loadTransferAlerts, 30000);
    return () => clearInterval(id);
  }, [loadTransferAlerts]);

  const token = localStorage.getItem(MARKET_TOKEN_KEY) || sessionStorage.getItem(MARKET_TOKEN_KEY);

  // ── Poll pending receipts count for admin (badge + toast on new upload) ──
  useEffect(() => {
    if (marketUser?.hub !== "admin" || !token) return;
    async function checkReceipts() {
      try {
        const r = await api.marketAdminPendingReceipts(token);
        const count = (r.pending_receipts || []).length;
        setPendingReceiptsCount(count);
        if (count > lastToastedCountRef.current) {
          const diff = count - lastToastedCountRef.current;
          toast(`${diff} new payment receipt${diff > 1 ? "s" : ""} uploaded — check the Receipts tab`, {
            duration: 6000,
            action: { label: "Go", onClick: () => setAdminTab("receipts") },
          });
          lastToastedCountRef.current = count;
        }
      } catch { /* silent */ }
    }
    checkReceipts();
    const id = setInterval(checkReceipts, 30000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketUser?.hub, token]);

  function logout() {
    localStorage.removeItem(MARKET_TOKEN_KEY);
    sessionStorage.removeItem(MARKET_TOKEN_KEY);
    localStorage.removeItem(MARKET_TERMS_KEY);
    setMarketUser(null);
    setShowAdmin(false);
    setAdminViewAs(null);
    toast.success("Logged out of Market");
  }

  function handleHubClick(hub, setupDone) {
    if (!setupDone) { setShowSetup(true); return; }
    // ── Admin Universal Access: bypass login for all hubs ──────────────────────
    if (marketUser?.hub === "admin") {
      if (hub === "admin") {
        // Return to admin panel view
        setAdminViewAs(null);
        setShowAdmin(true);
      } else {
        // Enter hub view without re-login (admin token already accepted by backend)
        setAdminViewAs(hub);
        setShowAdmin(false);
      }
      return;
    }
    setLoginHub(hub);
    setShowLogin(true);
  }

  function onLoginSuccess(res) {
    setMarketUser({
      hub: res.hub,
      name: res.name,
      phone: res.phone,
      carrier_name: res.carrier_name,
      carrier_agreement_signed: res.carrier_agreement_signed,
      carrier_agreement_signed_at: res.carrier_agreement_signed_at,
      carrier_agreement_signature_name: res.carrier_agreement_signature_name,
      staff_agreement_signed: res.staff_agreement_signed,
      staff_agreement_signed_at: res.staff_agreement_signed_at,
      staff_agreement_signature_name: res.staff_agreement_signature_name,
    });
    const termsOk = res.terms_accepted || localStorage.getItem(MARKET_TERMS_KEY) === "true";
    if (!termsOk) { setPendingToken(res.token); setShowTerms(true); }
    // Gate: unsigned carrier hits agreement modal immediately on hub entry
    else if (res.hub === "carrier" && !res.carrier_agreement_signed) { setShowAgreement(true); }
    // Gate: unsigned recruiter/setter hits staff agreement modal
    else if ((res.hub === "recruiter" || res.hub === "setter") && !res.staff_agreement_signed) { setShowStaffAgreement(true); }
    setShowLogin(false);
    if (res.hub === "admin") setShowAdmin(true);
  }

  // Auto-open bid modal after login if user arrived via ?bid= when not yet logged in
  useEffect(() => {
    if (!marketUser || !slugPendingDriver) return;
    const driver = slugPendingDriver;
    setSlugPendingDriver(null);
    // If terms haven't been accepted yet, defer until Terms Gate is dismissed
    const termsAlreadyOk = localStorage.getItem(MARKET_TERMS_KEY) === "true";
    if (!termsAlreadyOk) {
      // Store driver for after Terms acceptance; Terms Gate's onAccepted will fire openBidModal
      setPostTermsPendingDriver(driver);
      return;
    }
    // Slight delay so login modal is fully dismissed first
    setTimeout(() => openBidModal(driver), 300);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketUser]);

  // Poll for transfer payment notifications (carrier hub only)
  useEffect(() => {
    if (!marketUser || marketUser.hub !== "carrier" || !token) return;
    async function checkNotifications() {
      try {
        const r = await api.marketGetNotifications(token);
        const notifs = r.notifications || [];
        const paymentN   = notifs.filter(n => n.type !== "annulment");
        const annulmentN = notifs.filter(n => n.type === "annulment");
        // Payment notifications
        if (paymentN.length > 0) {
          setPaymentNotifs(paymentN);
          setPaymentNotifIndex((i) => Math.min(i, paymentN.length - 1));
        } else {
          setPaymentNotifs([]);
          setIsPaymentMinimized(false);
          setPaymentNotifIndex(0);
        }
        // Annulment notifications
        if (annulmentN.length > 0) {
          setAnnulmentNotifs(annulmentN);
        } else {
          setAnnulmentNotifs([]);
          setAnnulNotifIndex(0);
        }
      } catch { /* silent */ }
    }
    checkNotifications();
    const interval = setInterval(checkNotifications, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketUser?.hub, token]);

  function openBidModal(driver) {
    if (!marketUser) { setSlugPendingDriver(driver); setLoginHub("carrier"); setShowLogin(true); return; }
    // Check if carrier has signed the fleet agreement
    if (marketUser.hub === "carrier" && !marketUser.carrier_agreement_signed) {
      setPendingBidDriver(driver);
      setShowAgreement(true);
      return;
    }
    setBidDriver(driver);
    setShowBidModal(true);
  }

  async function cropFace(appId) {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok) return;
    try {
      await api.marketCropFace(tok, appId);
      toast.success("Face crop done!");
      loadMarketData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Crop failed.");
    }
  }

  async function handleBidStatusChange(bidId, status) {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok) return;
    try {
      await api.marketUpdateBidStatus(tok, bidId, status);
      toast.success(`Bid ${status}`);
      loadMarketData();
    } catch {
      toast.error("Failed to update bid.");
    }
  }

  async function handleAcknowledgeAnnulment(notifId) {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok) return;
    setAnnulAcking(true);
    try {
      await api.marketAcknowledgeAnnulment(tok, notifId);
      setAnnulmentNotifs(prev => prev.filter(n => n.notification_id !== notifId));
      setAnnulNotifIndex(i => Math.max(0, i));
      // Refresh history
      const h = await api.marketGetAnnulmentHistory(tok);
      setAnnulmentHistory(h.annulments || []);
    } catch { toast.error("Failed to acknowledge."); }
    finally { setAnnulAcking(false); }
  }

  // Load annulment history for carrier profile tab
  useEffect(() => {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok || marketUser?.hub !== "carrier") return;
    api.marketGetAnnulmentHistory(tok)
      .then(r => setAnnulmentHistory(r.annulments || []))
      .catch(() => {});
    // Also load doc credits
    api.marketCarrierDocCredits(tok)
      .then(r => setDocCredits(r))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketUser?.hub]);

  async function handleDeleteBid(bidId) {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok) return;
    try {
      await api.marketDeleteBid(tok, bidId);
      toast.success("Bid deleted");
      loadMarketData();
    } catch {
      toast.error("Failed to delete bid.");
    }
  }

  async function handleToggleVisibility(appId, visible) {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok) return;
    try {
      await api.marketToggleVisibility(tok, appId, visible);
      toast.success(visible ? "Driver is now visible on Market" : "Driver hidden from Market");
      loadMarketData();
    } catch {
      toast.error("Failed to update visibility.");
    }
  }

  async function handleUpdateDriverStatus(appId, market_status) {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok) return;
    try {
      await api.marketUpdateDriverStatus(tok, appId, market_status);
      const labels = { applied: "Applied", approved: "Approved ✓", not_approved: "Not Approved" };
      toast.success(`Driver status: ${labels[market_status] || market_status}`);
      loadMarketData();
    } catch {
      toast.error("Failed to update driver status.");
    }
  }

  async function handleSeedDemo() {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok) return;
    try {
      const res = await api.marketAdminSeedDemoDrivers(tok);
      toast.success(`Seeded ${res.seeded} demo drivers on the Market`);
      loadMarketData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Seed failed.");
    }
  }

  async function handleSeedFullMarket() {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok) return;
    try {
      toast.loading("Seeding full market with all real drivers + bid history…");
      const res = await api.marketAdminSeedFullMarket(tok);
      toast.dismiss();
      toast.success(
        `Done! ${res.real_apps_enabled} real drivers enabled · ${res.real_bids_added} bids created` +
        (res.demo_drivers_added > 0 ? ` · ${res.demo_drivers_added} demo drivers added` : "")
      );
      loadMarketData();
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.detail || "Seed failed.");
    }
  }

  async function handleLoadRealDrivers() {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok) return;
    try {
      toast.loading("Loading all 20 real CSV drivers onto the Market…");
      const res = await api.marketAdminLoadRealDrivers(tok);
      toast.dismiss();
      toast.success(
        `Done! ${res.demo_drivers_deleted} demo drivers removed · ` +
        `${res.drivers_upserted_or_updated} real drivers activated · ` +
        `${res.bids_added} bids seeded`
      );
      loadMarketData();
    } catch (err) {
      toast.dismiss();
      toast.error(err?.response?.data?.detail || "Load failed.");
    }
  }

  async function handleTransferDriver(appId, bidId, carrierName) {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok) return;
    try {
      await api.marketTransferDriver(tok, appId, bidId);
      toast.success(`Driver transferred to ${carrierName}`);
      loadMarketData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed.");
    }
  }

  async function handleEnableAllConsent() {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok) return;
    try {
      const res = await api.marketAdminEnableAllConsent(tok);
      toast.success(`${res.updated} applications pushed to Market`);
      loadMarketData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed.");
    }
  }

  async function handleCropAllFaces() {
    const tok = localStorage.getItem(MARKET_TOKEN_KEY);
    if (!tok) return;
    toast.info("Cropping faces... this may take a moment.");
    try {
      const res = await api.marketAdminCropAllFaces(tok);
      toast.success(`Face crop complete: ${res.cropped} done, ${res.failed} failed`);
      loadMarketData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Crop all failed.");
    }
  }

  const [adminChatRoom, setAdminChatRoom] = useState("carriers_recruiters");

  const ADMIN_CHAT_ROOMS = [
    { id: "carriers_recruiters", label: "Carriers & Recruiters" },
    { id: "recruiters_safety",   label: "Recruiters & Safety"   },
    { id: "drivers_recruiters",  label: "Drivers & Recruiters"  },
    { id: "drivers_only",        label: "Drivers Global"        },
  ];

  const chatRoom =
    marketUser?.hub === "carrier" ? "carriers_recruiters"
    : marketUser?.hub === "admin" ? adminChatRoom
    : null;
  const chatLabel = marketUser?.hub === "admin"
    ? (ADMIN_CHAT_ROOMS.find(r => r.id === adminChatRoom)?.label || "Chat")
    : "Carriers & Recruiters Chat";

  // ── Market protection: clipboard override ──
  useEffect(() => {
    const handler = (e) => {
      const sel = window.getSelection()?.toString();
      if (!sel) return;
      e.clipboardData?.setData("text/plain", sel + "\n\n© Hylas Companion — hylascompanion.com");
      e.preventDefault();
    };
    document.addEventListener("copy", handler);
    return () => document.removeEventListener("copy", handler);
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: DARK }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: ACCENT }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" style={{ background: DARK }}>
      {/* ── Referral Welcome Banner ── */}
      {referralCode && !marketUser && (
        <div
          className="w-full px-4 py-3 flex items-center justify-center gap-3 text-center"
          style={{ background: "linear-gradient(90deg, #0e1f10 0%, #132415 50%, #0e1f10 100%)", borderBottom: "1px solid rgba(74,222,128,0.3)" }}
          data-testid="referral-welcome-banner"
        >
          <div className="w-2 h-2 rounded-full" style={{ background: "#4ADE80" }} />
          <p className="text-sm text-white/80">
            You were <strong className="text-white">invited by a Hylas driver</strong> — apply and earn extra perks.{" "}
            <button
              onClick={() => setShowDriverJoin(true)}
              className="font-head font-black uppercase tracking-[0.12em] text-[11px] underline underline-offset-2 hover:no-underline transition-all"
              style={{ color: "#4ADE80" }}
              data-testid="referral-banner-apply-btn"
            >
              Apply Now →
            </button>
          </p>
        </div>
      )}
      <Helmet>
        <title>Hylas Companion Market — CDL Driver Recruitment Transfer Board | All 48 States</title>
        <meta name="description" content="The Hylas Companion Market is a live CDL truck driver recruitment platform. Carriers bid on pre-screened drivers, recruiters manage placements, and drivers get top pay. Serving all 48 states." />
        <meta name="keywords" content="CDL driver recruitment, truck driver transfer market, carrier bidding, driver placement fees, CDL-A drivers available, trucking recruitment platform, driver marketplace, semi truck drivers, box truck drivers" />
        <meta property="og:title" content="Hylas Companion Market — CDL Driver Transfer Board" />
        <meta property="og:description" content="Live carrier bidding on pre-screened CDL drivers. Recruiters, carriers, and safety teams work together to place top drivers with the best carriers." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://hylascompanion.com/market" />
        <meta name="twitter:title" content="Hylas Companion Market — CDL Driver Transfer Board" />
        <meta name="twitter:description" content="Live bidding on pre-screened CDL drivers. Carriers compete, drivers win. All 48 states." />
        {/* GEO targeting */}
        <meta name="geo.region" content="US" />
        <meta name="geo.placename" content="United States" />
        <meta name="DC.coverage" content="United States — All 48 States" />
        {/* JSON-LD Structured Data for GEO/AI engines */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": "https://hylascompanion.com/market",
              "name": "Hylas Companion Driver Recruitment Market",
              "description": "A competitive bidding platform where trucking carriers bid on pre-screened CDL drivers. The market serves semi-truck and box-truck drivers across all 48 US states.",
              "url": "https://hylascompanion.com/market",
              "provider": {
                "@type": "Organization",
                "name": "Hylas Companion Trucking",
                "url": "https://hylascompanion.com",
                "telephone": "+1-708-395-9565",
                "email": "contact@hylascompanion.com",
                "areaServed": { "@type": "Country", "name": "United States" }
              },
              "serviceType": "CDL Driver Recruitment",
              "areaServed": [
                { "@type": "Country", "name": "United States" },
                { "@type": "AdministrativeArea", "name": "Illinois" },
                { "@type": "AdministrativeArea", "name": "Texas" },
                { "@type": "AdministrativeArea", "name": "Florida" },
                { "@type": "AdministrativeArea", "name": "Georgia" },
                { "@type": "AdministrativeArea", "name": "Tennessee" },
                { "@type": "AdministrativeArea", "name": "Mississippi" },
                { "@type": "AdministrativeArea", "name": "North Carolina" }
              ]
            },
            {
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "How does the Hylas Companion Driver Market work?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Drivers apply through Hylas Companion and are screened by the Safety team. Approved driver profiles go live on the Market. Carriers log in to the Carrier Hub, browse available drivers, and place competitive bids including a placement fee, CPM rate, and pay program. The Hylas team selects the best match and notifies the winning carrier by email." }
                },
                {
                  "@type": "Question",
                  "name": "What are the placement fee ranges on the Market?",
                  "acceptedAnswer": { "@type": "Answer", "text": "For semi-truck placements, the placement fee ranges from $1,500 to $2,100. For box-truck placements, the fee ranges from $800 to $1,400. All fees are in $100 increments and each new bid must exceed the current highest bid by at least $100." }
                },
                {
                  "@type": "Question",
                  "name": "What states does the Hylas Companion Market serve?",
                  "acceptedAnswer": { "@type": "Answer", "text": "The Hylas Companion Market serves all 48 contiguous US states, with a concentration of drivers in Illinois, Texas, Florida, Georgia, Tennessee, Mississippi, and North Carolina." }
                },
                {
                  "@type": "Question",
                  "name": "What CDL driver pay programs are available?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Carriers can offer Company Driver (CPM-based, minimum $0.70/mile), Lease Purchase (minimum 80% gross), Owner Operator (minimum 85% gross), or Percentage-based pay programs. Box-truck placements use CPM starting from $0.55/mile." }
                },
                {
                  "@type": "Question",
                  "name": "How are drivers protected on the market?",
                  "acceptedAnswer": { "@type": "Answer", "text": "Driver phone numbers and personal identifying information are never shared with carriers. Only the driver's name, state, CDL class, and photo are visible to carriers. Full contact details are only accessible to Hylas Companion admin and recruiter staff." }
                }
              ]
            }
          ]
        })}</script>
      </Helmet>
      <MarketStyleTag />
      {/* ── Market protection: clipboard override + print block ── */}
      <style>{`
        @media print {
          .driver-card-protected, .market-driver-grid { display: none !important; }
          body::after { content: "© Hylas Companion — Unauthorized reproduction prohibited."; display: block; text-align: center; padding: 40px; font-size: 18px; }
        }
        .driver-card-protected { user-select: none; }
      `}</style>
      <FloatingOrbs />
      <GrainOverlay />
      <ScanlineBeam />

      {/* ── PROMO BANNER (gold) with shimmer ── */}
      <div
        data-testid="market-promo-banner"
        className="fixed top-20 left-0 right-0 z-40 flex items-center justify-center gap-2 py-2 px-4 text-center overflow-hidden"
        style={{ background: GOLD, boxShadow: `0 4px 20px rgba(245,200,66,0.4)` }}
      >
        <Trophy className="w-3 h-3 shrink-0" style={{ color: DARK }} />
        <p className="font-head text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: DARK }}>
          FREE LAUNCH ACCESS — Market subscriptions 100% free during our loyalty promo period
        </p>
        <Trophy className="w-3 h-3 shrink-0" style={{ color: DARK }} />
        <ShimmerSweep color="rgba(255,255,255,0.25)" duration="3.5s" delay="1.2s" />
      </div>

      {/* ══════════════════════════════════════════════════════
          ── DRAMATIC HERO SECTION ──────────────────────────
          ══════════════════════════════════════════════════ */}
      <div
        data-testid="market-hero-section"
        className="relative overflow-hidden pt-28 pb-14 px-6"
        style={{
          background: `radial-gradient(ellipse 70% 55% at 50% -5%, rgba(255,77,0,0.28) 0%, transparent 65%), #0A0A0A`,
          borderBottom: `1px solid rgba(255,77,0,0.15)`,
        }}
      >
        {/* Scanline texture overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `repeating-linear-gradient(
              0deg,
              transparent,
              transparent 3px,
              rgba(255,255,255,0.012) 3px,
              rgba(255,255,255,0.012) 4px
            )`,
          }}
        />
        {/* Grid dots overlay */}
        <div
          className="absolute inset-0 pointer-events-none opacity-20"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(255,77,0,0.15) 1px, transparent 1px)`,
            backgroundSize: "48px 48px",
          }}
        />

        <div className="max-w-[1600px] mx-auto relative">
          {/* Eyebrow */}
          <motion.p
            initial={{ opacity: 0, letterSpacing: "1.5em" }}
            animate={{ opacity: 1, letterSpacing: "0.6em" }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="font-head text-[10px] uppercase text-white/30 mb-3"
          >
            Hylas Companion
          </motion.p>

          {/* Giant heading */}
          <div className="mb-6 overflow-hidden">
            <motion.h1
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-head font-black uppercase leading-none"
              style={{ fontSize: "clamp(3.5rem, 8vw, 7rem)", letterSpacing: "-0.01em" }}
            >
              <span className="text-white">HYLAS </span>
              <motion.span
                animate={{ textShadow: [
                  "0 0 80px rgba(255,77,0,0.4), 0 0 160px rgba(255,77,0,0.15)",
                  "0 0 120px rgba(255,77,0,0.65), 0 0 240px rgba(255,77,0,0.25)",
                  "0 0 80px rgba(255,77,0,0.4), 0 0 160px rgba(255,77,0,0.15)",
                ]}}
                transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                style={{ color: ACCENT }}
              >
                MARKET
              </motion.span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="font-head text-sm uppercase tracking-[0.35em] text-white/35 mt-3"
            >
              The Ultimate Driver Transfer &amp; Bidding Floor
            </motion.p>
          </div>

          {/* Live stats strip with count-up */}
          <motion.div
            variants={STAGGER_CONTAINER}
            initial="hidden"
            animate="show"
            className="flex flex-wrap items-center gap-6 mt-2"
          >
            {/* Drivers count-up */}
            <motion.div variants={FADE_UP} className="flex items-center gap-3">
              <LiveDot color={ACCENT} size={10} />
              <div>
                <p className="font-head font-black text-3xl leading-none" style={{ color: ACCENT }}>
                  {driverCountDisplay}
                </p>
                <p className="font-head text-xs uppercase tracking-[0.25em] text-white/30 mt-0.5">
                  Drivers on Market
                </p>
              </div>
            </motion.div>

            <div className="w-px h-10 bg-white/10" />

            {/* Live bids count-up */}
            <motion.div variants={FADE_UP} className="flex items-center gap-3">
              <LiveDot color={GOLD} size={10} />
              <div>
                <p className="font-head font-black text-3xl leading-none" style={{ color: GOLD }}>
                  {bidCountDisplay}
                </p>
                <p className="font-head text-xs uppercase tracking-[0.25em] text-white/30 mt-0.5">
                  Live Bids
                </p>
              </div>
            </motion.div>

            <div className="w-px h-10 bg-white/10" />

            {/* Hired this month count-up */}
            <motion.div variants={FADE_UP} className="flex items-center gap-3" data-testid="hero-hired-this-month">
              <TrendingUp className="w-5 h-5" style={{ color: "#22C55E" }} />
              <div>
                <p className="font-head font-black text-3xl leading-none" style={{ color: "#22C55E" }}>
                  {hiredCountDisplay}
                </p>
                <p className="font-head text-xs uppercase tracking-[0.25em] text-white/30 mt-0.5">
                  Hired — 30 Days
                </p>
              </div>
            </motion.div>

            <div className="w-px h-10 bg-white/10" />

            {/* Promo */}
            <motion.div variants={FADE_UP} className="flex items-center gap-3">
              <Flame className="w-5 h-5" style={{ color: ACCENT }} />
              <div>
                <p className="font-head font-black text-sm uppercase tracking-[0.05em] leading-none" style={{ color: ACCENT }}>
                  100% FREE LAUNCH
                </p>
                <p className="font-head text-[9px] uppercase tracking-[0.25em] text-white/30 mt-0.5">
                  No subscription cost
                </p>
              </div>
            </motion.div>

            {/* User pill (if logged in) */}
            {marketUser && (
              <>
                <div className="w-px h-10 bg-white/10" />
                <motion.button
                  variants={FADE_UP}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setShowAdmin((a) => !a)}
                  className="flex items-center gap-2 px-3 py-2 border border-white/15 text-white/70 hover:border-[#FF4D00] hover:text-[#FF4D00] transition-colors"
                >
                  {marketUser.hub === "admin" ? (
                    <Settings className="w-3.5 h-3.5" />
                  ) : (
                    <Award className="w-3.5 h-3.5" />
                  )}
                  <span className="font-head text-[10px] uppercase tracking-[0.15em]">
                    {marketUser.name}
                  </span>
                </motion.button>
              </>
            )}
          </motion.div>
        </div>
      </div>

      {/* ── DRIVER JOIN BANNER (visible to everyone who hasn't logged in as driver) ── */}
      {(!marketUser || marketUser.hub !== "driver") && (
        <div
          data-testid="driver-join-banner"
          className="relative overflow-hidden px-6 py-7 flex flex-col sm:flex-row items-center gap-6"
          style={{ background: "linear-gradient(90deg, rgba(255,77,0,0.10) 0%, rgba(255,77,0,0.04) 100%)", borderBottom: "1px solid rgba(255,77,0,0.18)" }}
        >
          <ShimmerSweep color="rgba(255,255,255,0.04)" duration="5s" delay="0s" />
          <div className="flex items-center gap-4 flex-1">
            <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: "rgba(255,77,0,0.12)", border: "1px solid rgba(255,77,0,0.3)" }}>
              <Truck className="w-5 h-5" style={{ color: ACCENT }} />
            </div>
            <div>
              <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-0.5">CDL Drivers</p>
              <p className="font-head text-base font-black uppercase tracking-[0.04em] text-white">
                Get Hired Faster — Join the Hylas Market
              </p>
              <p className="text-xs text-white/40 mt-0.5 max-w-lg">
                Add your profile to the board. Carriers compete to hire you. No spam — your phone stays private. Takes 2 minutes.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <a href="/loyalty" className="font-head text-[10px] uppercase tracking-[0.15em] text-white/40 hover:text-white/70 transition-colors underline underline-offset-4">
              See driver bonuses
            </a>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowDriverJoin(true)}
              data-testid="driver-join-market-btn"
              className="px-6 py-3 font-head text-xs font-black uppercase tracking-[0.2em] transition-all hover:brightness-110 flex items-center gap-2"
              style={{ background: ACCENT, color: "white" }}
            >
              <Zap className="w-3.5 h-3.5" /> Join Market
            </motion.button>
          </div>
        </div>
      )}

      {/* ── Main layout: sidebar + content ── */}
      <div
        className="flex max-w-[1600px] mx-auto"
        style={{ minHeight: "calc(100vh - 300px)" }}
      >
        {/* Hub Sidebar */}
        <HubSidebar
          marketUser={marketUser}
          setupComplete={setupComplete}
          onHubClick={handleHubClick}
          onLogout={logout}
          adminViewAs={adminViewAs}
        />

        {/* Main content area */}
        <main className="flex-1 overflow-x-hidden px-5 py-7 space-y-10">

          {/* ── Pricing page shortcut ── */}
          <div className="flex justify-end">
            <a
              href="https://hylascompanion.com/market/pricing"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="market-pricing-link-btn"
              className="flex items-center gap-2 px-4 py-2 font-head text-[10px] uppercase tracking-[0.2em] font-black border transition-all hover:brightness-110"
              style={{ borderColor: "rgba(245,200,66,0.35)", color: "#F5C842", background: "rgba(245,200,66,0.06)" }}
            >
              <Tag className="w-3.5 h-3.5" />
              View Carrier Pricing
            </a>
          </div>
          {leaderboard.length > 0 && marketUser?.hub === "carrier" && (
            <section data-testid="carrier-leaderboard-section">
              <div className="flex items-center gap-3 mb-4">
                <Trophy className="w-5 h-5 shrink-0" style={{ color: GOLD }} />
                <div>
                  <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-0.5">Ranked</p>
                  <h2 className="font-head text-xl font-black uppercase tracking-[0.04em]" style={{ color: GOLD }}>
                    Carrier Leaderboard
                  </h2>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2.5">
                {leaderboard.map((c, i) => {
                  const rankColor = i === 0 ? GOLD : i === 1 ? "#C0C0C0" : i === 2 ? "#CD7F32" : "rgba(255,255,255,0.25)";
                  return (
                    <div
                      key={c.carrier_name}
                      data-testid={`leaderboard-rank-${i + 1}`}
                      className="flex items-center gap-3 px-4 py-3 transition-all"
                      style={{
                        background: i === 0 ? "rgba(245,200,66,0.07)" : "rgba(255,255,255,0.025)",
                        border: `1px solid ${i === 0 ? "rgba(245,200,66,0.22)" : "rgba(255,255,255,0.07)"}`,
                      }}
                    >
                      <span
                        className="font-head font-black text-2xl leading-none shrink-0 w-8"
                        style={{ color: rankColor }}
                      >
                        #{i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-head text-sm font-black uppercase tracking-[0.02em] text-white truncate">
                          {c.carrier_name}
                        </p>
                        <p className="font-head text-[10px] mt-0.5" style={{ color: "rgba(255,255,255,0.35)" }}>
                          {c.bid_count} bid{c.bid_count !== 1 ? "s" : ""}
                          {c.top_bid ? ` · Top $${c.top_bid.toLocaleString()}` : ""}
                        </p>
                      </div>
                      {i === 0 && <Trophy className="w-3.5 h-3.5 shrink-0" style={{ color: GOLD }} />}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* ── Admin "Viewing As Hub" banner ───────────────────────────────────── */}
          {marketUser?.hub === "admin" && adminViewAs && (
            <div
              data-testid="admin-viewing-as-banner"
              className="flex items-center gap-3 px-4 py-3 mb-2"
              style={{ background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.3)", borderLeft: "3px solid #A78BFA" }}
            >
              <Settings className="w-4 h-4 shrink-0" style={{ color: "#A78BFA" }} />
              <p className="font-head text-xs uppercase tracking-[0.15em] flex-1" style={{ color: "rgba(255,255,255,0.6)" }}>
                Admin — viewing{" "}
                <span className="font-black text-white">{adminViewAs.charAt(0).toUpperCase() + adminViewAs.slice(1)} Hub</span>
                {" "}(read/write access active)
              </p>
              <button
                data-testid="admin-return-to-admin-btn"
                onClick={() => { setAdminViewAs(null); setShowAdmin(true); }}
                className="font-head text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 transition-all hover:brightness-110"
                style={{ background: "rgba(167,139,250,0.15)", color: "#A78BFA", border: "1px solid rgba(167,139,250,0.35)" }}
              >
                ← Back to Admin Hub
              </button>
            </div>
          )}

          {/* Admin panel */}
          {showAdmin && marketUser?.hub === "admin" && !adminViewAs && (
            <div id="tour-admin-board" style={{ scrollMarginTop: 80 }}>

              {/* ── Admin Tab Nav ── */}
              <div className="flex gap-1 flex-wrap mb-6 p-1 rounded-lg" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                {[
                  { key: "market",     label: "Market",         icon: TrendingUp,  badge: 0 },
                  { key: "receipts",   label: "Receipts",       icon: Award,       badge: pendingReceiptsCount },
                  { key: "payment",    label: "Payment Status", icon: Building2,   badge: 0 },
                  { key: "agreements", label: "Agreements",     icon: FileText,    badge: 0 },
                  { key: "profiles",   label: "Profiles",       icon: Users,       badge: 0 },
                  { key: "email",      label: "Email Accounts", icon: Settings,    badge: 0 },
                  { key: "tour",       label: "Tour Editor",    icon: Star,        badge: 0 },
                ].map(({ key, label, icon: Icon, badge }) => (
                  <button
                    key={key}
                    data-testid={`admin-tab-${key}`}
                    onClick={() => {
                      setAdminTab(key);
                      if (key === "receipts") {
                        lastToastedCountRef.current = pendingReceiptsCount;
                      }
                    }}
                    className="relative flex items-center gap-2 px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-widest transition-all"
                    style={{
                      background: adminTab === key ? ACCENT : "transparent",
                      color:      adminTab === key ? "#fff" : "rgba(255,255,255,0.45)",
                    }}
                  >
                    <Icon size={13} />
                    {label}
                    {badge > 0 && (
                      <span
                        data-testid={`admin-tab-${key}-badge`}
                        className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-black text-white"
                        style={{ background: "#EF4444" }}
                      >
                        {badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* ── Tab Panels ── */}
              {adminTab === "market" && (
                <AdminPanel
                  user={marketUser}
                  token={token}
                  drivers={drivers}
                  bids={bids}
                  onCropFace={cropFace}
                  onBidStatusChange={handleBidStatusChange}
                  onSeedDemo={handleSeedDemo}
                  onSeedFullMarket={handleSeedFullMarket}
                  onLoadRealDrivers={handleLoadRealDrivers}
                  onEnableAllConsent={handleEnableAllConsent}
                  onCropAllFaces={handleCropAllFaces}
                  onTransferDriver={handleTransferDriver}
                  onRefresh={loadMarketData}
                />
              )}

              {adminTab === "receipts" && (
                <PendingReceiptsPanel token={token} onRefresh={loadMarketData} />
              )}

              {adminTab === "payment" && (
                <PaymentStatusPanel token={token} />
              )}

              {adminTab === "agreements" && (
                <AgreementsPanel token={token} />
              )}

              {adminTab === "profiles" && (
                <ProfilesPanel token={token} onRefresh={loadMarketData} />
              )}

              {adminTab === "email" && (
                <EmailAccountsPanel token={token} />
              )}

              {adminTab === "tour" && (
                <TourEditor token={token} />
              )}
            </div>
          )}

          {/* ── Drivers Hub View ── */}
          {(marketUser?.hub === "driver" || (marketUser?.hub === "admin" && adminViewAs === "driver")) && (
            <div id="tour-driver-hub" style={{ scrollMarginTop: 80 }}>
              <DriversHub token={token} apiBase={apiBase} />
            </div>
          )}

          {/* ── Recruiter Hub View (also shown when admin enters recruiter view) ── */}
          {(marketUser?.hub === "recruiter" || (marketUser?.hub === "admin" && adminViewAs === "recruiter")) && (
            <div id="tour-recruiter-hub" style={{ scrollMarginTop: 80 }}>
              <RecruiterHub
                token={token}
                drivers={drivers}
                onToggleVisibility={handleToggleVisibility}
                onCropFace={cropFace}
              />
            </div>
          )}

          {/* ── Safety Hub View (also shown when admin enters safety view) ── */}
          {(marketUser?.hub === "safety" || (marketUser?.hub === "admin" && adminViewAs === "safety")) && (
            <div id="tour-safety-hub" style={{ scrollMarginTop: 80 }}>
              <SafetyHub
                token={token}
                drivers={drivers}
                onUpdateDriverStatus={handleUpdateDriverStatus}
                onRefresh={loadMarketData}
                marketUser={marketUser}
              />
            </div>
          )}

          {/* ── Available Drivers / Carrier Hub ── */}
          {(!marketUser || marketUser?.hub === "carrier" || (marketUser?.hub === "admin" && !adminViewAs)) && (
            <CarrierHub
              marketUser={marketUser}
              leaderboard={leaderboard}
              bids={bids}
              drivers={drivers}
              token={token}
              openBidModal={openBidModal}
              setShowAgreement={setShowAgreement}
              docCredits={docCredits}
              setCarrierDocUploadBid={setCarrierDocUploadBid}
              annulmentHistory={annulmentHistory}
              setPaymentNotif={setPaymentNotif}
              setShowPaymentModal={setShowPaymentModal}
              transferAlerts={transferAlerts}
              dataLoading={dataLoading}
              setupComplete={setupComplete}
              handleHubClick={handleHubClick}
              apiBase={apiBase}
              setSelectedDriver={setSelectedDriver}
            />
          )} {/* END carrier/admin/unauthenticated driver view */}

          {/* ── Live Bids Feed (Carrier + Admin only, not when admin is viewing another hub) ── */}
          {bids.length > 0 && !["driver", "recruiter", "safety"].includes(marketUser?.hub) && !adminViewAs && (
            <section>
              <div className="mb-4">
                <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">
                  Real-Time
                </p>
                <h2
                  className="font-head text-2xl font-black uppercase tracking-[0.04em]"
                  style={{ color: GOLD }}
                >
                  Live Bid Board
                </h2>
              </div>
              <motion.div
                data-testid="live-bids-feed"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }}
              >
                <motion.div
                  variants={STAGGER_CONTAINER}
                  initial="hidden"
                  animate="show"
                  className="divide-y divide-white/05 max-h-96 overflow-y-auto"
                >
                  {bids.slice(0, 20).map((b) => {
                    const statusColor =
                      b.status === "transferred"
                        ? GOLD
                        : b.status === "declined"
                        ? "#ef4444"
                        : ACCENT;
                    return (
                      <motion.div
                        key={b.id}
                        variants={SLIDE_LEFT}
                        className="px-5 py-4 flex flex-wrap items-start gap-3"
                        style={{ animation: b.status === "live" ? "hm-bid-flash 1.5s ease-out" : undefined }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-head text-base font-bold text-white">{b.carrier_name}</span>
                            <span className="text-white/30 text-sm">bid</span>
                            <span className="font-head text-lg font-black" style={{ color: GOLD }}>
                              ${b.placement_fee?.toLocaleString()}
                            </span>
                            <span className="text-white/30 text-sm">on</span>
                            <span className="text-white/70 text-base">{b.driver_name}</span>
                            {b.pay_program && b.pay_program !== "company_driver" && (
                              <span className="font-head text-xs font-bold uppercase px-2 py-1" style={{ background: "rgba(255,77,0,0.12)", color: ACCENT, border: "1px solid rgba(255,77,0,0.25)" }}>
                                {b.driver_pay_percentage}% {b.pay_program.replace("_", " ")}
                              </span>
                            )}
                            {b.pay_program === "company_driver" && b.driver_pay_cpm && (
                              <span className="font-head text-xs font-bold uppercase px-2 py-1" style={{ background: "rgba(255,77,0,0.12)", color: ACCENT, border: "1px solid rgba(255,77,0,0.25)" }}>
                                {b.driver_pay_cpm}¢ CPM
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-white/45 mt-1">
                            {b.position_type?.replace("_", " ")} · {b.equipment_package} ·{" "}
                            {b.payment_option?.replace(/_/g, " ")}
                            {b.weekly_flex_price && ` · $${b.weekly_flex_price}/wk`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {b.status === "live" && <LiveDot color={ACCENT} size={9} />}
                          <span
                            className="px-2.5 py-1 font-head text-xs uppercase tracking-[0.15em]"
                            style={{ background: `${statusColor}15`, color: statusColor, border: `1px solid ${statusColor}40` }}
                          >
                            {b.status}
                          </span>
                          <span className="text-sm text-white/40">{timeAgo(b.created_at)}</span>
                          {/* Admin-only delete */}
                          {marketUser?.hub === "admin" && (
                            deletingBidId === b.id ? (
                              <div className="flex items-center gap-1">
                                <button
                                  data-testid={`delete-bid-confirm-${b.id}`}
                                  onClick={() => { handleDeleteBid(b.id); setDeletingBidId(null); }}
                                  className="px-2 py-1 font-head text-[10px] font-black uppercase tracking-[0.12em]"
                                  style={{ background: "rgba(239,68,68,0.18)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.4)" }}>
                                  Yes
                                </button>
                                <button onClick={() => setDeletingBidId(null)}
                                  className="p-1 text-white/30 hover:text-white/60">
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <button
                                data-testid={`delete-bid-btn-${b.id}`}
                                onClick={() => setDeletingBidId(b.id)}
                                title="Delete bid"
                                className="p-1.5 text-white/20 hover:text-red-400 transition-colors"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </motion.div>
            </section>
          )}

          {/* ── Chat ── */}
          {chatRoom && token && (
            <section>
              <div className="mb-4">
                <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">
                  Live Communication
                </p>
                <h2
                  className="font-head text-2xl font-black uppercase tracking-[0.04em]"
                  style={{ color: ACCENT }}
                >
                  {chatLabel}
                </h2>
              </div>
              {/* Admin room selector */}
              {marketUser?.hub === "admin" && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {ADMIN_CHAT_ROOMS.map((r) => (
                    <button
                      key={r.id}
                      data-testid={`admin-chat-tab-${r.id}`}
                      onClick={() => setAdminChatRoom(r.id)}
                      className="font-head text-[9px] uppercase tracking-[0.15em] px-3 py-1.5 transition-all"
                      style={{
                        background: adminChatRoom === r.id ? ACCENT : "rgba(255,255,255,0.05)",
                        color: adminChatRoom === r.id ? "#fff" : "rgba(255,255,255,0.45)",
                        border: `1px solid ${adminChatRoom === r.id ? ACCENT : "rgba(255,255,255,0.1)"}`,
                      }}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              )}
              <ChatRoom room={chatRoom} roomLabel={chatLabel} token={token} hub={marketUser?.hub} />
            </section>
          )}

          {/* ── How It Works (unauthenticated only) ── */}
          {!marketUser && (
            <section
              data-testid="how-it-works-section"
              className="pt-4"
              style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
            >
              <div className="mb-8">
                <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">
                  Platform Guide
                </p>
                <h2
                  className="font-head text-2xl font-black uppercase tracking-[0.04em] text-white"
                >
                  How the Market Works
                </h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  {
                    num: "01",
                    icon: Truck,
                    t: "Drivers Apply",
                    d: "Apply via Hylas website. Safety vets and approves drivers for the Market.",
                    c: ACCENT,
                  },
                  {
                    num: "02",
                    icon: Building2,
                    t: "Carriers Bid",
                    d: "See driver cards (name, state, face only) and place live bids with your offer.",
                    c: GOLD,
                  },
                  {
                    num: "03",
                    icon: Users,
                    t: "Recruiters Close",
                    d: "Manage deals, update status, communicate with all parties to seal placement.",
                    c: GOLD,
                  },
                  {
                    num: "04",
                    icon: Star,
                    t: "Admin Approves",
                    d: "Only Hylas admin approves the winning bid. Transparency at every step.",
                    c: ACCENT,
                  },
                ].map((s) => {
                  const Icon = s.icon;
                  const accentRgb = s.c === ACCENT ? "255,77,0" : "245,200,66";
                  return (
                    <div
                      key={s.t}
                      className="p-5 relative overflow-hidden group transition-all duration-300"
                      style={{
                        background: MID,
                        border: `1px solid rgba(255,255,255,0.07)`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.border = `1px solid rgba(${accentRgb},0.3)`;
                        e.currentTarget.style.boxShadow = `0 0 24px rgba(${accentRgb},0.1)`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.border = `1px solid rgba(255,255,255,0.07)`;
                        e.currentTarget.style.boxShadow = "none";
                      }}
                    >
                      {/* Number */}
                      <div
                        className="absolute top-3 right-4 font-head font-black text-4xl leading-none opacity-10"
                        style={{ color: s.c }}
                      >
                        {s.num}
                      </div>
                      <div
                        className="w-10 h-10 flex items-center justify-center mb-4"
                        style={{
                          background: `rgba(${accentRgb},0.1)`,
                          border: `1px solid rgba(${accentRgb},0.2)`,
                        }}
                      >
                        <Icon className="w-5 h-5" style={{ color: s.c }} />
                      </div>
                      <p className="font-head text-sm font-bold uppercase tracking-[0.12em] text-white mb-2">
                        {s.t}
                      </p>
                      <p className="text-[11px] text-white/40 leading-relaxed">{s.d}</p>
                    </div>
                  );
                })}
              </div>

              {/* CTA strip */}
              <div
                className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-5"
                style={{
                  background: `rgba(255,77,0,0.06)`,
                  border: `1px solid rgba(255,77,0,0.15)`,
                }}
              >
                <div>
                  <p className="font-head font-black text-base uppercase tracking-[0.05em] text-white">
                    Ready to hire your next driver?
                  </p>
                  <p className="text-white/40 text-xs mt-0.5">
                    Join the Carrier Hub and start bidding — 100% free during launch.
                  </p>
                </div>
                <button
                  onClick={() => handleHubClick("carrier", setupComplete)}
                  className="font-head text-xs font-black uppercase tracking-[0.25em] px-6 py-3 whitespace-nowrap transition-all hover:brightness-110"
                  style={{ background: ACCENT, color: "white" }}
                >
                  Enter Carrier Hub →
                </button>
              </div>
            </section>
          )}

          {/* Footer */}
          <p className="text-center text-white/20 text-[10px] pb-4">
            <Link
              to="/market/terms"
              className="underline hover:text-white/50 transition-colors"
            >
              Marketplace Terms
            </Link>
            {" · "}All driver data is protected. Carriers see name, state and cropped face only.
          </p>
        </main>
      </div>

      {/* ── Modals ── */}
      {googleAuthLoading && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center" style={{ background: "rgba(0,0,0,0.92)" }}>
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#FF4D00" }} />
            <p className="font-head text-sm uppercase tracking-[0.2em] text-white/60">Signing in with Google…</p>
          </div>
        </div>
      )}
      {showSetup && (
        <SetupWizard onComplete={() => { setSetupComplete(true); setShowSetup(false); }} />
      )}
      {showLogin && (
        <LoginModal hub={loginHub} onSuccess={onLoginSuccess} onClose={() => setShowLogin(false)} />
      )}
      {resetToken && (
        <ResetPasswordModal
          resetToken={resetToken}
          onClose={() => { setResetToken(""); window.history.replaceState({}, "", window.location.pathname); }}
          onSuccess={() => { setResetToken(""); setShowLogin(true); window.history.replaceState({}, "", window.location.pathname); }}
        />
      )}
      {showTerms && (
        <TermsGate
          token={pendingToken || token}
          onAccepted={() => {
            setShowTerms(false);
            // Chain: if carrier still needs to sign agreement, show it next
            if (marketUser?.hub === "carrier" && !marketUser?.carrier_agreement_signed) {
              setShowAgreement(true);
              return;
            }
            // Chain: if recruiter/setter still needs to sign staff agreement, show it next
            if ((marketUser?.hub === "recruiter" || marketUser?.hub === "setter") && !marketUser?.staff_agreement_signed) {
              setShowStaffAgreement(true);
              return;
            }
            // If a bid was triggered before terms were accepted, resume it now
            if (postTermsPendingDriver) {
              const d = postTermsPendingDriver;
              setPostTermsPendingDriver(null);
              setTimeout(() => openBidModal(d), 300);
            }
          }}
        />
      )}
      {showDriverJoin && (
        <DriverSelfApplyModal onClose={() => setShowDriverJoin(false)} referralCode={referralCode} />
      )}
      {/* ── Bid modal ── */}
      <AnimatePresence>
        {showBidModal && bidDriver && (
          <BidModal
            driver={bidDriver}
            token={token}
            onClose={() => { setShowBidModal(false); setBidDriver(null); }}
            onSuccess={loadMarketData}
            cashbackBalance={marketUser?.cashback_balance || 0}
            onAgreementRequired={() => {
              const d = bidDriver;
              setBidDriver(null);
              setShowBidModal(false);
              setPendingBidDriver(d);
              setShowAgreement(true);
            }}
          />
        )}
      </AnimatePresence>

      {/* Carrier Fleet Agreement Modal */}
      <AnimatePresence>
        {showAgreement && (
          <CarrierAgreementModal
            token={token}
            carrierName={marketUser?.carrier_name || marketUser?.name || ""}
            onAgreed={(agreementData) => {
              // Update local marketUser so they don't see agreement again this session
              setMarketUser(u => u ? { ...u, carrier_agreement_signed: true, ...agreementData } : u);
              setShowAgreement(false);
              // Now open the bid modal for the pending driver
              if (pendingBidDriver) {
                setBidDriver(pendingBidDriver);
                setPendingBidDriver(null);
                setShowBidModal(true);
              }
            }}
            onClose={() => {
              // Unsigned carrier clicking × → logout, no silent bypass
              // Signed carrier re-reading from profile → just close
              if (!marketUser?.carrier_agreement_signed) {
                logout();
              } else {
                setShowAgreement(false);
              }
              setPendingBidDriver(null);
            }}
          />
        )}
        {showStaffAgreement && (
          <StaffAgreementModal
            token={token}
            hub={marketUser?.hub}
            staffName={marketUser?.name || ""}
            onAgreed={(sigData) => {
              setMarketUser(u => u ? { ...u, staff_agreement_signed: true, staff_agreement_signed_at: sigData.signed_at, staff_agreement_signature_name: sigData.signature_name } : u);
              setShowStaffAgreement(false);
            }}
            onClose={() => {
              if (!marketUser?.staff_agreement_signed) logout();
              else setShowStaffAgreement(false);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedDriver && (
          <CarrierDriverProfile
            driver={selectedDriver}
            token={token}
            onClose={() => setSelectedDriver(null)}
            onBid={openBidModal}
            canBid={!!(marketUser?.hub === "carrier" || marketUser?.hub === "admin")}
          />
        )}
      </AnimatePresence>

      {/* ── Transfer Payment Modal — notification-based (carrier, minimizable) ── */}
      <AnimatePresence>
        {paymentNotifs.length > 0 && !isPaymentMinimized && (
          <TransferPaymentModal
            notification={paymentNotifs[paymentNotifIndex]}
            token={token}
            currentIndex={paymentNotifIndex}
            total={paymentNotifs.length}
            onPrev={() => setPaymentNotifIndex(i => Math.max(0, i - 1))}
            onNext={() => setPaymentNotifIndex(i => Math.min(paymentNotifs.length - 1, i + 1))}
            onMinimize={() => setIsPaymentMinimized(true)}
            onClose={() => setIsPaymentMinimized(true)}
            onReceiptUploaded={() => setIsPaymentMinimized(true)}
          />
        )}
      </AnimatePresence>

      {/* ── Transfer Payment Modal — manual My-Bids "Upload Receipt" ── */}
      <AnimatePresence>
        {showPaymentModal && paymentNotif && (
          <TransferPaymentModal
            notification={paymentNotif}
            token={token}
            onClose={() => { setShowPaymentModal(false); setPaymentNotif(null); }}
            onReceiptUploaded={() => { setShowPaymentModal(false); setPaymentNotif(null); }}
          />
        )}
      </AnimatePresence>

      {/* ── Annulment Notice Modal (carrier only) ── */}
      <AnimatePresence>
        {annulmentNotifs.length > 0 && (
          <AnnulmentNoticeModal
            notices={annulmentNotifs}
            currentIndex={annulNotifIndex}
            total={annulmentNotifs.length}
            onPrev={() => setAnnulNotifIndex(i => Math.max(0, i - 1))}
            onNext={() => setAnnulNotifIndex(i => Math.min(annulmentNotifs.length - 1, i + 1))}
            onAcknowledge={handleAcknowledgeAnnulment}
            loading={annulAcking}
          />
        )}
      </AnimatePresence>

      {/* ── Carrier Doc Upload Modal (earn $30 credit) ── */}
      <AnimatePresence>
        {carrierDocUploadBid && (
          <CarrierDocUploadModal
            bid={carrierDocUploadBid}
            token={token}
            onClose={() => setCarrierDocUploadBid(null)}
            onDone={() => {
              setCarrierDocUploadBid(null);
              api.marketCarrierDocCredits(token).then(r => setDocCredits(r)).catch(() => {});
            }}
          />
        )}
      </AnimatePresence>

      {/* ── Tour Wizard ── */}
      {marketUser?.hub && showTour && (
        <TourWizard hub={marketUser.hub} onClose={closeTour} />
      )}

      {/* Pulsing "NOT PAID" badge — sits above ? tour button, visible when payment popup is minimized */}
      {paymentNotifs.length > 0 && isPaymentMinimized && (
        <button
          data-testid="payment-badge"
          onClick={() => { setIsPaymentMinimized(false); setPaymentNotifIndex(0); }}
          title="You have unpaid transfers — click to open"
          className="fixed z-[8995] payment-badge-pulse flex flex-col items-center justify-center gap-0.5"
          style={{
            width: 52, height: 52,
            bottom: 54,
            right: 24,
            background: "#0A0A0A",
            border: "1px solid rgba(239,68,68,0.55)",
          }}
        >
          <span className="font-head text-[7px] font-black uppercase tracking-[0.1em]" style={{ color: "#ef4444" }}>NOT PAID</span>
          <span className="font-head text-lg font-black leading-none" style={{ color: "#ef4444" }}>{paymentNotifs.length}</span>
        </button>
      )}

      {/* Help button — re-opens tour after it's been dismissed */}
      {marketUser?.hub && !showTour && (
        <button
          data-testid="reopen-tour-btn"
          onClick={openTour}
          title="Restart platform tour"
          className="fixed bottom-6 right-6 z-[8990] flex items-center justify-center transition-all hover:scale-110"
          style={{
            width: 36, height: 36,
            background: "#111116",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.35)",
            borderRadius: 0,
            boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
          }}
        >
          <span className="font-head text-xs font-black">?</span>
        </button>
      )}
    </div>
  );
}
